import { Transition, TransitionChild } from '@headlessui/react';
import { ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/solid';
import React, { Fragment, useEffect, useState } from 'react';
import { configuration } from '../../services/configuration';
import { SharedLink } from '../../services/models/shared-link';
import { StoryTweet } from '../../services/models/story-tweet';
import { ExternalUser } from '../../services/models/external-user';
import * as sharedLinksManager from '../../services/sharedlinks';
import { classNames } from '../../utils/class-names';
import Panel from '../ui/panel';

// Native replacement for the reader.newslit.co iframe app: the article view
// stays a backend-rendered /storytext iframe, but tweets/shares load through
// core's authenticated Apollo client — no refresh-token/postMessage handshake.

interface ReaderPanelProps {
  url: string | null;
  storyId: number | null;
  show: boolean;
  onClose: (value: boolean) => void;
}

export function ReaderPanel({ url, storyId, show, onClose }: ReaderPanelProps) {
  const [sharedLink, setSharedLink] = useState<SharedLink>();
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    setSharedLink(undefined);
    setSheetOpen(false);

    if (!show || !storyId) {
      return;
    }

    let cancelled = false;
    sharedLinksManager
      .getSharedLink(storyId)
      .then(result => {
        if (!cancelled) {
          setSharedLink(result);
        }
      })
      .catch(() => {
        // The share bar is progressive enhancement over the article view;
        // swallow the error just like the legacy reader did.
        if (!cancelled) {
          setSharedLink(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [show, storyId]);

  const tweets = sharedLink?.tweets ?? [];

  return (
    <Panel isOpen={show} onClose={() => onClose(false)}>
      {url && storyId && (
        <div className="relative flex flex-col h-full divide-y divide-gray-200 overflow-hidden">
          <div className="min-h-0 flex-1 flex flex-col overflow-y-scroll">
            <div className="relative flex-1">
              <iframe
                title="Newslit Reader"
                src={`${configuration.backend.baseUrl}/storytext?url=${encodeURIComponent(url)}`}
                className="w-full h-full border-none"
              />
            </div>
          </div>

          {tweets.length > 0 && (
            <>
              <div className="shrink-0 flex justify-start">
                <button
                  type="button"
                  className="flex cursor-pointer justify-between w-full px-4 py-4 bg-white hover:bg-transparent text-gray-600 hover:text-gray-900 items-center text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-white"
                  onClick={() => setSheetOpen(true)}
                >
                  <ShareBar
                    shareCount={sharedLink!.shareCount}
                    externalUsers={sharedLink!.externalUsers}
                  />
                </button>
              </div>
              <TweetsSheet open={sheetOpen} onDismiss={() => setSheetOpen(false)} tweets={tweets} />
            </>
          )}
        </div>
      )}
    </Panel>
  );
}

interface TweetsSheetProps {
  open: boolean;
  onDismiss: () => void;
  tweets: StoryTweet[];
}

// Bottom sheet for the tweet list (replaces react-spring-bottom-sheet, which
// never supported React 18).
function TweetsSheet({ open, onDismiss, tweets }: TweetsSheetProps) {
  return (
    <Transition show={open} as={Fragment}>
      <div className="absolute inset-0 z-30 overflow-hidden">
        <TransitionChild
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-500/75" aria-hidden="true" onClick={onDismiss} />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="transform transition ease-in-out duration-300"
          enterFrom="translate-y-full"
          enterTo="translate-y-0"
          leave="transform transition ease-in-out duration-300"
          leaveFrom="translate-y-0"
          leaveTo="translate-y-full"
        >
          <div
            role="dialog"
            aria-label="What people are saying"
            className="absolute inset-x-0 bottom-0 h-3/5 flex flex-col rounded-t-xl bg-white shadow-xl"
          >
            <div className="shrink-0 flex items-center justify-between px-4 py-3">
              <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-300" />
              <button
                type="button"
                className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-hidden"
                onClick={onDismiss}
              >
                <span className="sr-only">Close tweets</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <ul className="divide-y divide-gray-200">
                {tweets.map((tweet, index) => (
                  <li key={index} className="relative py-4 hover:bg-gray-50">
                    <TweetItem tweet={tweet} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Transition>
  );
}

interface TweetItemProps {
  tweet: StoryTweet;
}

function TweetItem({ tweet }: TweetItemProps) {
  if (!tweet) return null;

  return (
    <a
      href={tweet.externalUrl}
      target="_blank"
      rel="noreferrer"
      className="block hover:bg-gray-50 hover:no-underline focus:outline-hidden"
    >
      <div className="flex space-x-3 px-4">
        <img className="h-10 w-10 rounded-full" src={tweet.imageUrl} alt="" />
        <div className="flex-1 space-y-1">
          <div className="flex-1">
            <div className="flex items-center justify-start">
              <div className="text-sm font-medium text-gray-900 overflow-hidden">{tweet.name}</div>
              <div className="ml-1 text-sm font-normal text-gray-500 truncate">
                @{tweet.username}
              </div>
            </div>
            <div className="flex whitespace-nowrap text-xs text-gray-500">
              <time dateTime={tweet.dateCreated?.toString()} className="whitespace-nowrap">
                Commented {tweet.time}
              </time>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-700">
              <span
                dangerouslySetInnerHTML={{
                  __html: tweet.bodyHtml.replace(/href/g, "target='_blank' class='underline' href"),
                }}
              />
            </p>
          </div>
        </div>
      </div>
    </a>
  );
}

interface ShareBarProps {
  shareCount: number;
  externalUsers: ExternalUser[];
}

function ShareBar({ shareCount, externalUsers }: ShareBarProps) {
  return (
    <>
      <span>
        <span className="inline-block whitespace-nowrap text-center rounded-sm align-middle mr-2 font-bold text-gray-700 bg-gray-200 text-base py-1 px-2 share-count">
          {shareCount}
        </span>
        {externalUsers?.map((externalUser, index) => {
          const isLast = index === externalUsers.length - 1;

          return (
            <img
              key={index}
              src={externalUser.imageUrl}
              alt={externalUser.username}
              className={classNames(
                // Negative margin overlaps the avatar row (`mr-n1` was the
                // legacy reader's Bootstrap-style name; Tailwind never
                // generated it).
                !isLast ? '-mr-1' : '',
                'inline w-7 h-7 rounded-full ring-2 ring-white'
              )}
              loading="lazy"
            />
          );
        })}
      </span>
      <ChevronUpIcon className="h-6 w-6" />
    </>
  );
}
